import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Users } from 'lucide-react';
import { Button } from '../ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { TableConfigModal } from './TableConfigModal';
import localDatabase from '../../services/localDatabase';
import { publishCrossTab } from '../../services/crossTab';
import { pushPendingToSupabase } from '../../services/supabaseSync';
import { checkReservationConflicts, doTimesOverlap } from '../../services/conflictChecker';
import { calculateTableStatus, TableWithStatus } from '../../utils/tableStatus';
import type { Table, Shift, Reservation } from '../../types';
import toast from 'react-hot-toast';
import { useTenant } from '../../contexts/TenantContext';
import { isWalkInSystemCustomer } from '../../lib/customerClassification';

interface TableFloorPlanProps {
  tables: Table[];
  shifts: Shift[];
  reservations: Reservation[];
  selectedShift: string;
  onShiftChange: (shiftId: string) => void;
  onTableUpdate?: (tableId: string, updates: Partial<Table>) => void;
}

const getTableStatusColor = (status: TableWithStatus['displayStatus']) => {
  switch (status) {
    case 'available': return 'bg-emerald-500 border-emerald-600 text-white shadow-lg';
    case 'occupied': return 'bg-red-500 border-red-600 text-white shadow-lg';
    case 'reserved': return 'bg-blue-500 border-blue-600 text-white shadow-lg';
    // case 'maintenance': return 'bg-amber-500 border-amber-600 text-white shadow-lg';
    // case 'cleaning': return 'bg-violet-500 border-violet-600 text-white shadow-lg';
    default: return 'bg-slate-500 border-slate-600 text-white shadow-lg';
  }
};

const getTableStatusIcon = (status: TableWithStatus['displayStatus']) => {
  switch (status) {
    case 'available': return '🟢';
    case 'occupied': return '🔴';
    case 'reserved': return '🔵';
    // case 'maintenance': return '🟡';
    // case 'cleaning': return '🟣';
    default: return '⚪';
  }
};

const toMinutes = (hhmm: string): number => {
  const [h, m] = (hhmm || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

// Supports overnight shifts (e.g. 22:00-02:00) and end-of-day "24:00".
const isTimeWithinShift = (time: string, shift: Shift | undefined): boolean => {
  if (!shift || !time) return false;
  const target = toMinutes(time);
  const start = toMinutes(shift.start_time);
  let end = toMinutes(shift.end_time);
  if (shift.end_time === '24:00') end = 24 * 60;

  if (end > start) {
    return target >= start && target < end;
  }
  return target >= start || target < end;
};

export const TableFloorPlan: React.FC<TableFloorPlanProps> = ({
  tables,
  shifts,
  reservations,
  selectedShift,
  onShiftChange,
  onTableUpdate
}) => {
  const { tenantId } = useTenant();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [viewMode, setViewMode] = useState<'floor' | 'list'>('floor');
  const [zoneFilter, setZoneFilter] = useState<string>('all');
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const controlsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (controlsRef.current) {
        setIsNarrow(controlsRef.current.offsetWidth < 650);
      }
    });

    if (controlsRef.current) {
      resizeObserver.observe(controlsRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const tablesWithStatus: TableWithStatus[] = tables
    .filter(table => zoneFilter === 'all' || table.zone === zoneFilter)
    .map(table => calculateTableStatus(table, reservations, selectedShift, currentTime));

  const tablesByZone = tablesWithStatus.reduce((acc, table) => {
    if (!acc[table.zone]) acc[table.zone] = [];
    acc[table.zone].push(table);
    return acc;
  }, {} as Record<string, TableWithStatus[]>);

  const zones = Array.from(
    new Set(
      tables
        .map(t => t.zone)
        .filter((z): z is string => !!z && z.trim().length > 0)
    )
  );

  const handleTableConfig = (table: Table) => {
    setSelectedTable(table);
    setShowConfigModal(true);
  };

  const handleTableUpdate = (updates: Partial<Table>) => {
    if (selectedTable && onTableUpdate) {
      onTableUpdate(selectedTable.id, updates);
    }
  };

  const activeShift = shifts.find((s) => s.id === selectedShift);
  const unassignedReservations = reservations.filter((r) => {
    // Only confirmed reservations should be assignable from this modal.
    if (r.status !== 'confirmed') return false;
    if (r.table_id) return false;
    if (r.shift_id !== selectedShift) return false;
    if (!isTimeWithinShift(r.time, activeShift)) return false;
    return true;
  });

  const getValidReservationsForTable = (table: Table) => {
    // Get existing confirmed/pending/seated reservations on this table
    const existingReservations = reservations.filter(r => 
      r.table_id === table.id && 
      r.shift_id === selectedShift &&
      ['confirmed', 'in-progress'].includes(r.status)
    );

    return unassignedReservations.filter(r => {
      // Hide synthetic walk-in/system customers from manual reservation seating list
      if (isWalkInSystemCustomer(r.customer as any)) return false;
      // 1. Capacity check
      if (r.party_size > table.seats) return false;

      // 2. Time conflict check
      const hasConflict = existingReservations.some(existing => 
        doTimesOverlap(r.time, existing.time)
      );
      
      return !hasConflict;
    });
  };

  const seatReservationAtTable = async (reservationId: string) => {
    if (!selectedTable) return;
    if (!tenantId) {
      toast.error('Δεν έχει επιλεγεί επιχείρηση. Παρακαλώ συνδεθείτε ξανά.');
      return;
    }
    try {
      const table = tables.find(t => t.id === selectedTable.id);
      const reservation = reservations.find(r => r.id === reservationId);
      if (!table || !reservation) return;

      // Capacity check
      if (table.seats < reservation.party_size) {
        toast.error('Το τραπέζι δεν χωράει την παρέα.');
        return;
      }

      // Check for conflicts using the centralized conflict checker (checks both local and Supabase)
      const conflictCheck = await checkReservationConflicts({
        tenantId,
        date: reservation.date,
        time: reservation.time,
        shiftId: reservation.shift_id,
        tableId: table.id,
        excludeId: reservation.id
      });

      if (conflictCheck.hasConflict) {
        toast.error(conflictCheck.message || 'Υπάρχει χρονική σύγκρουση για αυτό το τραπέζι.');
        return;
      }

      // Show warning if Supabase check failed (offline or error)
      if (conflictCheck.conflictType === 'supabase_error') {
        toast(conflictCheck.message || 'Η ανάθεση θα επιβεβαιωθεί κατά τον συγχρονισμό', {
          icon: '⚠️',
          duration: 5000
        });
      }

      await localDatabase.updateReservation(reservationId, { table_id: table.id });
      setShowConfigModal(false);
      publishCrossTab({ type: 'sync:reservations' });
      toast.success('Η κράτηση ανατέθηκε στο τραπέζι.');
      if (navigator.onLine) {
        await pushPendingToSupabase();
      }
    } catch (err) {
      console.error('Seating reservation failed:', err);
      toast.error('Η ανάθεση τραπεζιού απέτυχε. Προσπαθήστε ξανά.');
    }
  };

  const unseatCurrentReservation = async (reservationId: string) => {
    try {
      await localDatabase.updateReservation(reservationId, { table_id: null } as any);
      setShowConfigModal(false);
      publishCrossTab({ type: 'sync:reservations' });
      toast.success('Το τραπέζι αποδεσμεύτηκε.');
      if (navigator.onLine) {
        await pushPendingToSupabase();
      }
    } catch (err) {
      console.error('Unseating reservation failed:', err);
      toast.error('Η αποδέσμευση τραπεζιού απέτυχε. Προσπαθήστε ξανά.');
    }
  };

  const selectedTableStatus = selectedTable
    ? calculateTableStatus(
        selectedTable,
        reservations,
        selectedShift,
        currentTime
      )
    : null;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Controls */}
      <div className="bg-card p-3 md:p-4 rounded-lg border border-border shadow-sm" ref={controlsRef}>
        <div className="tf-controls">
          <div className="tf-controls-grid">
            <div className="tf-controls-filters">
              <div className="tf-controls-field">
                <label className="text-sm font-medium text-foreground whitespace-nowrap">Βάρδια:</label>
              <Select value={selectedShift} onValueChange={onShiftChange}>
                <SelectTrigger className="w-[240px] max-w-full touch-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {shifts.filter(shift => shift.is_active).map((shift) => (
                    <SelectItem key={shift.id} value={shift.id}>
                      {isNarrow ? shift.name : `${shift.name} (${shift.start_time} - ${shift.end_time})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            
              <div className="tf-controls-field">
                <label className="text-sm font-medium text-foreground whitespace-nowrap">Ζώνη:</label>
              <Select value={zoneFilter} onValueChange={setZoneFilter}>
                <SelectTrigger className="w-[180px] max-w-full touch-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες</SelectItem>
                  {zones.map(zone => (
                    <SelectItem key={zone} value={zone}>{zone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </div>
          
            <div className="tf-controls-actions">
              <Button
                variant={viewMode === 'floor' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('floor')}
                className="gap-2 touch-target"
              >
                <MapPin className="h-4 w-4" />
                <span className="hidden sm:inline">Πλάνο</span>
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="gap-2 touch-target"
              >
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Λίστα</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Status Legend */}
      <div className="bg-card p-3 md:p-4 rounded-lg border border-border shadow-sm">
        <h3 className="text-xs md:text-sm font-medium text-foreground mb-3">Επεξήγηση Καταστάσεων</h3>
        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-4">
          {(['available', 'occupied', 'reserved'] as const).map(status => ( // Removed 'maintenance', 'cleaning' for v1 release
            <div key={status} className="flex items-center gap-2">
              <div className={cn("w-4 h-4 md:w-6 md:h-6 rounded-full border-2 shadow-md flex-shrink-0", getTableStatusColor(status).split(' ')[0])} />
              <span className="text-xs md:text-sm font-medium text-foreground truncate">
                {status === 'available' && 'Διαθέσιμο'}
                {status === 'occupied' && 'Κατειλημμένο'}
                {status === 'reserved' && 'Κρατημένο'}
                {/* {status === 'maintenance' && 'Συντήρηση'} */}
                {/* {status === 'cleaning' && 'Καθαρισμός'} */}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Floor Plan View */}
      {viewMode === 'floor' && (
        <div className="space-y-4 md:space-y-6 transform-gpu">
          {Object.entries(tablesByZone).map(([zone, zoneTables]) => (
            <div key={zone} className="bg-card p-4 md:p-6 rounded-lg border border-border shadow-sm">
              <h3 className="text-base md:text-lg font-semibold text-card-foreground mb-3 md:mb-4 flex items-center gap-2">
                <MapPin className="h-4 w-4 md:h-5 md:w-5 text-blue-500 flex-shrink-0" />
                <span className="truncate">{zone}</span>
                <span className="text-xs md:text-sm font-normal text-muted-foreground whitespace-nowrap">
                  ({zoneTables.length} τραπέζια)
                </span>
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4 transform-gpu">
                                {zoneTables.map((table) => (
                  <div
                    key={table.id}
                    className={cn(
                      "relative py-6 px-4 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-xl overflow-hidden transform-gpu will-change-transform",
                      getTableStatusColor(table.displayStatus)
                    )}
                    onClick={() => {
                      const originalTable = tables.find(t => t.id === table.id);
                      if (originalTable) {
                        handleTableConfig(originalTable);
                      }
                    }}
                                      >
                                         {/* Table Number - Always visible at top with subtle distinction */}
                     <div className="absolute top-1 left-1 z-20">
                       <div className="bg-black/40 px-2 py-1 rounded border border-white/30">
                         <div className="text-lg font-bold text-white">Τ{table.number}</div>
                       </div>
                     </div>

                     {/* Table Seats Info - Always visible on mobile, hidden on desktop when occupied */}
                    <div className={`absolute inset-0 flex items-center justify-center z-10 pl-8 ${table.currentReservation ? 'xl:hidden' : ''}`}>
                      <div className="text-base text-white font-medium flex items-center gap-2">
                        {table.seats}
                        <Users className="h-5 w-5" />
                      </div>
                    </div>

                     {/* Reservation Indicator - Subtle visual indicator without showing name */}
                    {table.currentReservation && (
                      <div className="absolute inset-0 bg-black/10 rounded-lg flex items-center justify-center z-5 pl-6">
                        <div className="text-white/90 text-sm font-medium hidden xl:block">
                          {table.displayStatus === 'occupied' ? 'Κατειλημμένο' : 'Κρατημένο'}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-accent border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground">Τραπέζι</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground">Ζώνη</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground">Καθίσματα</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground">Κατάσταση</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground">Τρέχουσα Κράτηση</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-card-foreground">Επόμενη Κράτηση</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tablesWithStatus.map((table) => (
                  <tr 
                    key={table.id} 
                    className="hover:bg-accent cursor-pointer" 
                    onClick={() => {
                      const originalTable = tables.find(t => t.id === table.id);
                      if (originalTable) {
                        handleTableConfig(originalTable);
                      }
                    }}
                  >
                    <td className="px-4 py-4">
                      <div className="font-bold text-lg">Τ{table.number}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-muted-foreground">{table.zone}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{table.seats}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                                             <div className={cn(
                         "inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium text-white shadow-md",
                         getTableStatusColor(table.displayStatus)
                       )}>
                         <span>{getTableStatusIcon(table.displayStatus)}</span>
                         {table.displayStatus === 'available' && 'Διαθέσιμο'}
                         {table.displayStatus === 'occupied' && 'Κατειλημμένο'}
                         {table.displayStatus === 'reserved' && 'Κρατημένο'}
                         {/* {table.displayStatus === 'maintenance' && 'Συντήρηση'} */}
                         {/* {table.displayStatus === 'cleaning' && 'Καθαρισμός'} */}
                       </div>
                    </td>
                    <td className="px-4 py-4">
                      {table.currentReservation ? (
                        <div className="text-sm">
                          <div className="font-medium">{table.currentReservation.customer?.name}</div>
                          <div className="text-muted-foreground">{table.currentReservation.time}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {table.nextReservation ? (
                        <div className="text-sm">
                          <div className="font-medium">{table.nextReservation.customer?.name}</div>
                          <div className="text-muted-foreground">{table.nextReservation.time}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && selectedTable && (
        <TableConfigModal
          table={selectedTable!}
          onUpdate={handleTableUpdate}
          zones={zones}
          reservations={selectedTable ? getValidReservationsForTable(selectedTable) : []}
          onSeat={seatReservationAtTable}
          currentReservation={selectedTableStatus?.currentReservation || null}
          onUnseat={selectedTableStatus?.currentReservation ? unseatCurrentReservation : undefined}
          onClose={() => {
            setShowConfigModal(false);
            setSelectedTable(null);
          }}
          isOpen={showConfigModal}
        />
      )}
    </div>
  );
};
